import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import FormSection from 'components/ui/form-section';
import { DEFAULT_CONVERSION_CURRENCY } from 'components/features/submission-details/types';
import SubmissionCurrencyTab from 'components/features/submission-currency-tabs';
import FormLayoutGroup from 'components/ui/form-layout-group';
import { Form, FormInstance, InputNumber } from 'antd';
import { formatLocaleNumber } from 'utils/format';
import {
  ContractAdjustmentPayloadDtoAdjustmentTypeEnum,
  ContractResponseDto,
} from 'libs/pinnacle-sdk';
import {
  BinderScheduleItem,
  calculateBinderSchedule,
  EBinderScheduleKey,
} from 'components/features/binding/contract-detail/binder-schedule/form';
import { useWatch } from 'antd/es/form/Form';
import BinderScheduleSection from 'components/features/binding/contract-detail/binder-schedule';
import { isEmpty, isNil } from 'lodash';
import { validateForm } from 'utils/form';
import { getValidationSchema } from '../form';

interface MidtermAdjustmentsSectionProps {
  contract: ContractResponseDto;
  form: FormInstance;
  setIsFormValid: (valid: boolean) => void;
}

const MidtermAdjustmentsSection: React.FC<MidtermAdjustmentsSectionProps> = ({
  form,
  contract,
  setIsFormValid,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>(
    contract.submission?.originalCurrency || DEFAULT_CONVERSION_CURRENCY
  );

  const [binderScheduleData, setBinderScheduleData] = useState<
    BinderScheduleItem[]
  >([]);

  const layerPremiumAdjustmentAmount = useWatch(
    'layerPremiumAdjustmentAmount',
    form
  );
  const adjustmentType = useWatch('adjustmentType', form);

  const binderShareSignedPercentage =
    Number(contract?.binderShareSigned ?? 0) * 100;

  // Check if rates should be editable (only for Rate Update)
  const canEditRates =
    adjustmentType ===
    ContractAdjustmentPayloadDtoAdjustmentTypeEnum.RateUpdate;

  const validateFormValues = useCallback(async () => {
    const values = form.getFieldsValue();
    const isValid = await validateForm(
      values,
      form,
      Object.keys(values),
      getValidationSchema(t, contract)
    );
    setIsFormValid(isValid);
  }, [adjustmentType, form]);

  useEffect(() => {
    if (
      adjustmentType ===
        ContractAdjustmentPayloadDtoAdjustmentTypeEnum.Cancellation &&
      contract.layer?.premium
    ) {
      const negatedPremium = -Number(contract.layer?.premium);
      form.setFieldValue('layerPremiumAdjustmentAmount', negatedPremium);
    }

    if (
      adjustmentType ===
      ContractAdjustmentPayloadDtoAdjustmentTypeEnum.RateUpdate
    ) {
      form.setFieldValue(
        'layerPremiumAdjustmentAmount',
        contract.layer?.premium
      );
    }

    if (
      adjustmentType ===
        ContractAdjustmentPayloadDtoAdjustmentTypeEnum.PolicyExtension ||
      adjustmentType ===
        ContractAdjustmentPayloadDtoAdjustmentTypeEnum.PremiumAdjustment ||
      adjustmentType ===
        ContractAdjustmentPayloadDtoAdjustmentTypeEnum.NonFinancialEndorsement
    ) {
      form.setFieldValue('layerPremiumAdjustmentAmount', 0);
    }
    validateFormValues();
  }, [adjustmentType, contract.layer?.premium, form]);

  // Initialize binder schedule data from contract on component mount
  useEffect(() => {
    if (contract && binderScheduleData.length === 0) {
      const initialSchedule = calculateBinderSchedule(
        [
          {
            key: EBinderScheduleKey.CEDING,
            label: t('contractDetail.cedingCommission'),
            rate: contract?.cedingCommissionRate
              ? Number(contract?.cedingCommissionRate) * 100
              : undefined,
            isNegative: true,
          },
          {
            key: EBinderScheduleKey.BROKERAGE,
            label: t('contractDetail.sourceBrokerage'),
            rate: contract?.sourceBrokerageRate
              ? Number(contract?.sourceBrokerageRate) * 100
              : undefined,
            isNegative: true,
          },
          {
            key: EBinderScheduleKey.ERE,
            label: t('contractDetail.ere'),
            rate: contract?.ereRate
              ? Number(contract?.ereRate) * 100
              : undefined,
            isNegative: true,
          },
          {
            key: EBinderScheduleKey.WITHHOLDING_TAX,
            label: t('contractDetail.withholdingTax'),
            rate: contract?.withholdingTaxRate
              ? Number(contract?.withholdingTaxRate) * 100
              : undefined,
            isNegative: true,
          },
          {
            key: EBinderScheduleKey.FEES,
            label: t('contractDetail.taxesAndFees'),
            amount: 0,
            isNegative: true,
          },
          {
            key: EBinderScheduleKey.OTHER_DEDUCTIONS,
            label: t('contractDetail.otherDeductions'),
            amount: 0,
            isNegative: true,
          },
          {
            key: EBinderScheduleKey.AIT,
            label: t('contractDetail.ait'),
            rate: contract?.aitRate
              ? Number(contract?.aitRate) * 100
              : undefined,
            isNegative: true,
          },
          {
            key: EBinderScheduleKey.GST_CEDING,
            label: t('contractDetail.gstCeding'),
            rate: contract?.gstRateOnCedingCommission
              ? Number(contract?.gstRateOnCedingCommission) * 100
              : undefined,
            isNegative: true,
          },
          {
            key: EBinderScheduleKey.GST_BROKERAGE,
            label: t('contractDetail.gstSourceBrokerage'),
            rate: contract?.gstRateOnSourceBrokerage
              ? Number(contract?.gstRateOnSourceBrokerage) * 100
              : undefined,
            isNegative: true,
          },
          {
            key: EBinderScheduleKey.GST_PREMIUM,
            label: t('contractDetail.gstPremium'),
            rate: contract?.gstRateOnPremium
              ? Number(contract?.gstRateOnPremium) * 100
              : undefined,
            isNegative: true,
          },
        ],
        {
          premium: Number(layerPremiumAdjustmentAmount) || 0,
          binderShareSigned: binderShareSignedPercentage,
          brokerCountry: contract?.broker?.country,
          layerTotalDeductions: contract?.layer?.totalDeductions,
          binderPinCommission: contract?.binder?.pinCommission,
        },
        t,
        canEditRates
      );

      // Update isEditable based on adjustment type
      const scheduleWithEditability = initialSchedule.map((item) => ({
        ...item,
        isEditable: item.isEditable ? canEditRates : false,
      }));

      setBinderScheduleData(scheduleWithEditability);
    }
  }, [
    contract,
    t,
    binderScheduleData.length,
    layerPremiumAdjustmentAmount,
    canEditRates,
  ]);

  // Recalculate amounts when layerPremiumAdjustmentAmount or canEditRates changes, preserving user input
  useEffect(() => {
    if (
      binderScheduleData.length > 0 &&
      (!isNil(layerPremiumAdjustmentAmount) || canEditRates)
    ) {
      const updatedSchedule = calculateBinderSchedule(
        binderScheduleData,
        {
          premium: Number(layerPremiumAdjustmentAmount || 0),
          binderShareSigned: binderShareSignedPercentage,
          brokerCountry: contract?.broker?.country,
          layerTotalDeductions: contract?.layer?.totalDeductions,
          binderPinCommission: contract?.binder?.pinCommission,
        },
        t,
        canEditRates
      );

      // Update isEditable based on adjustment type
      const scheduleWithEditability = updatedSchedule.map((item) => ({
        ...item,
        isEditable: item.isEditable ? canEditRates : false,
      }));

      setBinderScheduleData(scheduleWithEditability);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerPremiumAdjustmentAmount, canEditRates]);

  // Sync binder schedule data with form fields for submission
  useEffect(() => {
    if (!isEmpty(binderScheduleData)) {
      const cedingCommissionRate = binderScheduleData.find(
        (item) => item.key === EBinderScheduleKey.CEDING
      )?.rate;
      const sourceBrokerageRate = binderScheduleData.find(
        (item) => item.key === EBinderScheduleKey.BROKERAGE
      )?.rate;
      const ereRate = binderScheduleData.find(
        (item) => item.key === EBinderScheduleKey.ERE
      )?.rate;
      const gstOnEre = binderScheduleData.find(
        (item) => item.key === EBinderScheduleKey.GST_ON_ERE
      )?.rate;
      const otherDeductions = binderScheduleData.find(
        (item) => item.key === EBinderScheduleKey.OTHER_DEDUCTIONS
      )?.amount;
      const withholdingTaxRate = binderScheduleData.find(
        (item) => item.key === EBinderScheduleKey.WITHHOLDING_TAX
      )?.rate;
      const taxAndFeeAmount = binderScheduleData.find(
        (item) => item.key === EBinderScheduleKey.FEES
      )?.amount;
      const aitRate = binderScheduleData.find(
        (item) => item.key === EBinderScheduleKey.AIT
      )?.rate;
      const gstRateOnCedingCommission = binderScheduleData.find(
        (item) => item.key === EBinderScheduleKey.GST_CEDING
      )?.rate;
      const gstRateOnSourceBrokerage = binderScheduleData.find(
        (item) => item.key === EBinderScheduleKey.GST_BROKERAGE
      )?.rate;
      const gstRateOnPremium = binderScheduleData.find(
        (item) => item.key === EBinderScheduleKey.GST_PREMIUM
      )?.rate;
      const binderShareSigned = binderScheduleData.find(
        (item) => item.key === EBinderScheduleKey.PREMIUM
      )?.rate;
      form.setFieldsValue({
        cedingCommissionRate: !isNil(cedingCommissionRate)
          ? cedingCommissionRate / 100
          : undefined,
        sourceBrokerageRate: !isNil(sourceBrokerageRate)
          ? sourceBrokerageRate / 100
          : undefined,
        ereRate: !isNil(ereRate) ? ereRate / 100 : undefined,
        gstOnEre: !isNil(gstOnEre) ? gstOnEre / 100 : undefined,
        otherDeductions,
        withholdingTaxRate: !isNil(withholdingTaxRate)
          ? withholdingTaxRate / 100
          : undefined,
        taxAndFeeAmount,
        aitRate: !isNil(aitRate) ? aitRate / 100 : undefined,
        gstRateOnCedingCommission: !isNil(gstRateOnCedingCommission)
          ? gstRateOnCedingCommission / 100
          : undefined,
        gstRateOnSourceBrokerage: !isNil(gstRateOnSourceBrokerage)
          ? gstRateOnSourceBrokerage / 100
          : undefined,
        gstRateOnPremium: !isNil(gstRateOnPremium)
          ? gstRateOnPremium / 100
          : undefined,
        binderShareSigned: !isNil(binderShareSigned)
          ? binderShareSigned / 100
          : undefined,
      });
    }
  }, [binderScheduleData, form]);

  useEffect(() => {
    const totalInsurableValue = Number(
      contract.totalTiv ?? contract.submission?.totalTivView?.totalTiv ?? 0
    ).toFixed(2);
    if (totalInsurableValue) {
      form.setFieldsValue({
        totalInsurableValue: totalInsurableValue,
        layerPremiumAdjustmentAmount: 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract.submission?.totalTivView?.totalTiv]);

  useEffect(() => {
    if (canEditRates) {
      form.setFieldValue(
        'layerPremiumAdjustmentAmount',
        contract.layer?.premium || 0
      );
    }
  }, [canEditRates, contract.layer?.premium, form]);

  return (
    <FormSection title={t('contractAdjustment.midTermAdjustments')} collapsible>
      <FormLayoutGroup mdColumns={2}>
        <Form.Item
          name="totalInsurableValue"
          label={t('contractAdjustment.totalInsurableValue')}
        >
          <InputNumber<number>
            size="large"
            className="w-full"
            min={0}
            disabled={canEditRates || !adjustmentType}
            precision={2}
            formatter={formatLocaleNumber}
            placeholder={t('general.enter', {
              field: t('contractAdjustment.totalInsurableValue'),
            })}
          />
        </Form.Item>
        <Form.Item
          name="layerPremiumAdjustmentAmount"
          label={t('contractAdjustment.layerPremiumAdjustmentAmountProrated')}
        >
          <InputNumber<number>
            size="large"
            className="w-full"
            precision={2}
            formatter={formatLocaleNumber}
            placeholder={t('general.enter', {
              field: t('contractAdjustment.layerPremiumAdjustmentValue'),
            })}
            disabled={
              adjustmentType ===
                ContractAdjustmentPayloadDtoAdjustmentTypeEnum.Cancellation ||
              adjustmentType ===
                ContractAdjustmentPayloadDtoAdjustmentTypeEnum.NonFinancialEndorsement ||
              canEditRates ||
              !adjustmentType
            }
          />
        </Form.Item>
      </FormLayoutGroup>

      {/* Hidden fields to sync with binder schedule data for form submission */}
      {/* TODO: This is a temporary workaround. Future implementation would need to wrap the Form.Item around BinderScheduleSection, with valuePropName=binderSchedule and trigger=setBinderScheduleData */}
      <div className="hidden">
        <Form.Item name="cedingCommissionRate">
          <InputNumber />
        </Form.Item>
        <Form.Item name="sourceBrokerageRate">
          <InputNumber />
        </Form.Item>
        <Form.Item name="ereRate">
          <InputNumber />
        </Form.Item>
        <Form.Item name="gstOnEre">
          <InputNumber />
        </Form.Item>
        <Form.Item name="otherDeductions">
          <InputNumber />
        </Form.Item>
        <Form.Item name="withholdingTaxRate">
          <InputNumber />
        </Form.Item>
        <Form.Item name="taxAndFeeAmount">
          <InputNumber />
        </Form.Item>
        <Form.Item name="aitRate">
          <InputNumber />
        </Form.Item>
        <Form.Item name="gstRateOnCedingCommission">
          <InputNumber />
        </Form.Item>
        <Form.Item name="gstRateOnSourceBrokerage">
          <InputNumber />
        </Form.Item>
        <Form.Item name="gstRateOnPremium">
          <InputNumber />
        </Form.Item>
        <Form.Item name="binderShareSigned">
          <InputNumber />
        </Form.Item>
      </div>

      <SubmissionCurrencyTab
        originalCurrency={contract.submission?.originalCurrency}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        className="custom-tabs-with-bottom-curve-white"
        tabPosition="right"
      />

      <BinderScheduleSection
        currency={activeTab}
        originalCurrency={contract.submission?.originalCurrency}
        layerPremium={layerPremiumAdjustmentAmount}
        setBinderScheduleData={setBinderScheduleData}
        binderScheduleData={binderScheduleData}
        binderShareSigned={binderShareSignedPercentage}
        brokerCountry={contract?.broker?.country}
        layerTotalDeductions={contract?.layer?.totalDeductions}
        binderPinCommission={contract?.binder?.pinCommission}
        contractCurrencyRate={
          contract?.rateToDefaultCurrency
            ? Number(contract.rateToDefaultCurrency)
            : 1
        }
        setIsBinderScheduleValid={setIsFormValid}
        allowPremiumEdit={canEditRates}
      />
    </FormSection>
  );
};

export default MidtermAdjustmentsSection;
